import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Drawer, message } from 'antd';
import { ChatTabs } from './components/ChatTabs';
import { ChatConversation } from './components/ChatConversation';
import { ChatInput } from './components/ChatInput';
import { 
  generateConversationId,
  generateMessageId, 
  generateConversationTitle,
  type ChatConversation as ChatConversationType,
  type ChatMessage 
} from '@/services/chat';
import { createStreamingChat, type StreamController } from './utils/streamingUtils';

export interface ChatDrawerProps {
  open: boolean;
  onClose: () => void;
  width?: number | string;
  placement?: 'left' | 'right' | 'top' | 'bottom';
  title?: string;
  maxConversations?: number;
}

export const ChatDrawer: React.FC<ChatDrawerProps> = ({
  open,
  onClose,
  width = 480,
  placement = 'right',
  title = 'AI Assistant',
  maxConversations = 5,
}) => {
  // State management
  const [conversations, setConversations] = useState<ChatConversationType[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | undefined>();
  const [isStreaming, setIsStreaming] = useState(false);
  
  // Refs
  const streamControllerRef = useRef<StreamController | null>(null);

  // Get active conversation
  const activeConversation = conversations.find(conv => conv.id === activeConversationId);

  /**
   * Create a new conversation with UUID
   */
  const handleNewConversation = useCallback(() => {
    if (conversations.length >= maxConversations) {
      message.warning(`Maximum ${maxConversations} conversations allowed`);
      return;
    }

    const newConversationId = generateConversationId();
    const newConversation: ChatConversationType = {
      id: newConversationId,
      messages: [],
      lastActivity: new Date(),
    };

    setConversations(prev => [...prev, newConversation]);
    setActiveConversationId(newConversationId);
  }, [conversations.length, maxConversations]);

  /**
   * Close a conversation
   */
  const handleCloseConversation = useCallback((conversationId: string) => {
    setConversations(prev => {
      const filtered = prev.filter(conv => conv.id !== conversationId);
      
      // If we're closing the active conversation, switch to another one
      if (conversationId === activeConversationId) {
        const remainingConversations = filtered;
        if (remainingConversations.length > 0) {
          setActiveConversationId(remainingConversations[0].id);
        } else {
          setActiveConversationId(undefined);
        }
      }
      
      return filtered;
    });
  }, [activeConversationId]);

  /**
   * Switch to a conversation
   */
  const handleTabChange = useCallback((conversationId: string) => {
    setActiveConversationId(conversationId);
  }, []);

  /**
   * Add a message to a conversation
   */
  const addMessageToConversation = useCallback((
    conversationId: string, 
    message: Omit<ChatMessage, 'id'> & { id?: string }
  ) => {
    const messageWithId: ChatMessage = {
      id: message.id || generateMessageId(),
      ...message,
    };

    setConversations(prev => 
      prev.map(conv => {
        if (conv.id === conversationId) {
          const updatedMessages = [...conv.messages, messageWithId];
          const updatedConversation = {
            ...conv,
            messages: updatedMessages,
            lastActivity: new Date(),
          };

          // Generate title from first user message if not set
          if (!conv.title && message.isUser && message.content) {
            updatedConversation.title = generateConversationTitle(message.content);
          }

          return updatedConversation;
        }
        return conv;
      })
    );

    return messageWithId.id;
  }, []);

  /**
   * Update a specific message in a conversation
   */
  const updateMessageInConversation = useCallback((
    conversationId: string,
    messageId: string,
    updates: Partial<ChatMessage>
  ) => {
    setConversations(prev =>
      prev.map(conv => {
        if (conv.id === conversationId) {
          return {
            ...conv,
            messages: conv.messages.map(msg =>
              msg.id === messageId ? { ...msg, ...updates } : msg
            ),
            lastActivity: new Date(),
          };
        }
        return conv;
      })
    );
  }, []);

  /**
   * Send a message
   */
  const handleSendMessage = useCallback(async (messageContent: string) => {
    if (!activeConversationId || isStreaming) {
      return;
    }

    const activeConv = conversations.find(conv => conv.id === activeConversationId);
    if (!activeConv) return;

    // Add user message
    const userMessageId = addMessageToConversation(activeConversationId, {
      content: messageContent,
      isUser: true,
      timestamp: new Date(),
    });

    // Add assistant message placeholder
    const assistantMessageId = addMessageToConversation(activeConversationId, {
      content: '',
      isUser: false,
      timestamp: new Date(),
      isStreaming: true,
    });

    setIsStreaming(true);

    try {
      // Start streaming chat
      const streamController = createStreamingChat(
        activeConversationId,
        messageContent,
        (content: string, isComplete: boolean) => {
          updateMessageInConversation(activeConversationId, assistantMessageId, {
            content,
            isStreaming: !isComplete,
          });
        },
        {
          onComplete: (fullText: string) => {
            updateMessageInConversation(activeConversationId, assistantMessageId, {
              content: fullText,
              isStreaming: false,
            });
            setIsStreaming(false);
            streamControllerRef.current = null;
          },
          onError: (error: Error) => {
            console.error('Streaming error:', error);
            updateMessageInConversation(activeConversationId, assistantMessageId, {
              content: 'Sorry, there was an error processing your request.',
              isStreaming: false,
            });
            message.error('Failed to get response from AI assistant');
            setIsStreaming(false);
            streamControllerRef.current = null;
          },
        }
      );

      streamControllerRef.current = streamController;
    } catch (error) {
      console.error('Failed to send message:', error);
      updateMessageInConversation(activeConversationId, assistantMessageId, {
        content: 'Sorry, there was an error sending your message.',
        isStreaming: false,
      });
      message.error('Failed to send message');
      setIsStreaming(false);
    }
  }, [activeConversationId, isStreaming, conversations, addMessageToConversation, updateMessageInConversation]);

  /**
   * Stop streaming
   */
  const handleStopStreaming = useCallback(() => {
    if (streamControllerRef.current) {
      streamControllerRef.current.stop();
      streamControllerRef.current = null;
      setIsStreaming(false);
    }
  }, []);

  /**
   * Initialize with first draft conversation when drawer opens
   */
  useEffect(() => {
    if (open && conversations.length === 0) {
      handleNewConversation();
    }
  }, [open, conversations.length, handleNewConversation]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (streamControllerRef.current) {
        streamControllerRef.current.stop();
      }
    };
  }, []);

  const drawerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  };

  const contentStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: 0,
  };

  return (
    <Drawer
      title={title}
      placement={placement}
      onClose={onClose}
      open={open}
      width={width}
      styles={{
        body: { padding: 0 },
      }}
      destroyOnClose={false}
      keyboard
      mask={false} // No overlay for better UX
      style={drawerStyle}
    >
      <div style={contentStyle}>
        {/* Conversation Tabs */}
        <ChatTabs
          conversations={conversations}
          activeConversationId={activeConversationId}
          onTabChange={handleTabChange}
          onNewConversation={handleNewConversation}
          onCloseConversation={handleCloseConversation}
          loading={false}
          maxTabs={maxConversations}
        />

        {/* Conversation Content */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden' // Prevent outer scrolling, let inner components handle it
        }}>
          {activeConversation ? (
            <>
              <ChatConversation
                messages={activeConversation.messages}
                loading={false}
                style={{ 
                  flex: 1,
                  overflow: 'hidden' // ChatConversation will handle its own scrolling
                }}
              />
              
              <ChatInput
                onSend={handleSendMessage}
                loading={isStreaming}
                disabled={isStreaming}
                onStop={handleStopStreaming}
                showStopButton={true}
                placeholder="Ask me anything..."
              />
            </>
          ) : (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#8c8c8c',
            }}>
              No conversation selected
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default ChatDrawer;
